/**
 * InstallPrompt — bottom bar offering to install the PWA to the home screen.
 *
 * The caller controls WHEN this is eligible to appear via the `armed` prop
 * (see VehicleDetail: only after the user's first vehicle is sealed, never
 * on cold launch). `useInstallPrompt` controls WHETHER it's technically
 * possible right now (a captured `beforeinstallprompt` event, or iOS Safari
 * where we fall back to manual instructions).
 *
 * Capacitor readiness: this entire component becomes dead code once the app
 * is wrapped with Capacitor — a native wrap is installed via the App Store /
 * Play Store, not this web install flow. Do not "migrate" it; delete it at
 * wrap time. It uses only plain Web APIs (no plugin dependency needed).
 */

import { Button } from '@/components/Button';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { Download, Share, X } from 'lucide-react';

export interface InstallPromptProps {
  /** Only render when true — the caller decides the moment this becomes eligible. */
  armed: boolean;
}

export function InstallPrompt({ armed }: InstallPromptProps) {
  const { canInstall, isIOSFallback, dismissed, promptInstall, dismiss } = useInstallPrompt();

  if (!armed || dismissed || (!canInstall && !isIOSFallback)) return null;

  return (
    <div
      role="region"
      aria-label="Install Absolute Pinks"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 20,
        background: T.card,
        borderTop: `1px solid ${T.border}`,
        padding: `${SPACE.md}px max(${SPACE.md}px, env(safe-area-inset-left)) max(${SPACE.md}px, env(safe-area-inset-bottom)) max(${SPACE.md}px, env(safe-area-inset-right))`,
      }}
    >
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: SPACE.md,
        }}
      >
        <div style={{ flexShrink: 0, color: T.accent }}>
          {isIOSFallback ? (
            <Share size={20} aria-hidden="true" />
          ) : (
            <Download size={20} aria-hidden="true" />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{ margin: 0, fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.body }}
          >
            Keep Absolute Pinks a tap away
          </p>
          <p
            style={{
              margin: `${SPACE.xs}px 0 0`,
              fontSize: 12,
              color: T.textMuted,
              lineHeight: 1.5,
              fontFamily: F.body,
            }}
          >
            {isIOSFallback
              ? 'Tap the Share icon, then "Add to Home Screen" for quick access from the garage.'
              : 'Add it to your home screen for quick access from the garage — works offline too.'}
          </p>
        </div>

        {!isIOSFallback ? (
          <Button size="sm" variant="secondary" onClick={() => void promptInstall()}>
            Install
          </Button>
        ) : null}

        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          style={{
            flexShrink: 0,
            background: 'transparent',
            border: 'none',
            color: T.textMuted,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 44,
            minHeight: 44,
            borderRadius: RADIUS.md,
          }}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
